import { NgClass } from '@angular/common';
import { Component, forwardRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';

import { ColorPickerDirective } from 'ngx-color-picker';
import { Subscription } from 'rxjs';

import { FormcontrolErrorsComponent } from '../standalone/formcontrol-errors/formcontrol-errors.component';

import { ColorPickerColorsComponent } from './components';

@Component({
  selector: 'bn-web-ui-color-picker',
  styleUrls: ['color-picker.component.scss'],
  templateUrl: './color-picker.component.html',
  imports: [ReactiveFormsModule, NgClass, ColorPickerColorsComponent, FormcontrolErrorsComponent, ColorPickerDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => ColorPickerComponent),
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => ColorPickerComponent),
    },
  ],
})
export class ColorPickerComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {
  readonly #fb = inject(NonNullableFormBuilder);
  readonly errorMessage = 'Enter a valid hex color code.';

  @Input()
  public label?: string;

  @Input()
  public showColors = true;

  @Input()
  public colors!: string[];

  @Input()
  public readonly = false;

  public readonly presetColors = [
    '#1870C6',
    '#1B9CFC',
    '#23C9FC',
    '#21C2B6',
    '#1EBF7B',
    '#E1C330',
    '#FFAF48',
    '#FE7765',
    '#FF7854',
    '#FFC2B2',
    '#DF154D',
    '#B51018',
    '#C51E98',
    '#FA7DE5',
    '#8B54FD',
    '#5354FE',
    '#9D9FAC',
    '#897C71',
    '#AEA59D',
    '#D8CFC6',
    '#FFF1E4',
  ];

  public isTouched = false;
  public isFocused = false;

  readonly defaultColor = this.colors?.at(0) || this.presetColors?.at(0) || '#00000';
  readonly colorControl = this.#fb.control<string>(this.defaultColor, [Validators.required, Validators.pattern('^(#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}))?$')]);

  public onChange: (value: string) => void = () => ({});
  public onTouched: () => void = () => ({});

  readonly #subscription = new Subscription();

  ngOnInit(): void {
    this.colorControl.setValue(this.defaultColor);
    this.colors = this.colors?.length ? this.colors : this.presetColors;
    this.#subscription.add(this.colorControl.valueChanges.subscribe((value) => this.onChange(value)));
  }

  ngOnDestroy(): void {
    this.#subscription.unsubscribe();
  }

  public writeValue(value: string): void {
    if (value) {
      this.colorControl.setValue(value, { emitEvent: false });
    }
  }

  public registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = (): void => {
      fn();
      this.colorControl.markAsTouched();
    };
  }

  public validate(_control: AbstractControl): ValidationErrors | null {
    return this.colorControl?.valid ? null : { invalidForm: { valid: false, message: this.errorMessage } };
  }

  public setDisabledState(disabled: boolean): void {
    if (disabled) this.colorControl.disable();
    else this.colorControl.enable();
  }

  public onTouch(): void {
    this.isFocused = false;

    if (this.isTouched) {
      return;
    }

    this.isTouched = true;
    this.onTouched();
  }

  public onFocus(): void {
    this.isFocused = true;
  }

  public handleColorClick(color: string): void {
    this.colorControl.setValue(color);
  }
}
